/**
 * Form field variable naming for workflow builder UI (matches WorkflowExecutionService).
 */

/**
 * Prefer the live React Flow canvas graph when available. Server-loaded workflows
 * populate the canvas from Inertia props while workflow-context nodes/edges may
 * still be empty until nodes are added in-session.
 *
 * @param {Array<unknown>} liveNodes
 * @param {Array<unknown>} liveEdges
 * @param {Array<unknown>} contextNodes
 * @param {Array<unknown>} contextEdges
 */
export function resolveWorkflowGraphForVariableDiscovery(
  liveNodes,
  liveEdges,
  contextNodes,
  contextEdges,
) {
  const nodes = liveNodes?.length > 0 ? liveNodes : (contextNodes ?? [])
  const edges = liveEdges?.length > 0 ? liveEdges : (contextEdges ?? [])

  return { nodes, edges }
}

export function cleanFormNameForVariables(formName) {
  return String(formName || 'Form')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')
    .toLowerCase()
}

export function buildFormFieldVariableName(formName, fieldType, occurrenceIndex) {
  const clean = cleanFormNameForVariables(formName)
  const suffix = occurrenceIndex === 1 ? '' : String(occurrenceIndex)
  return `${clean}.${fieldType}${suffix}`
}

export function buildFormFieldLabelVariableName(formName, fieldType, occurrenceIndex) {
  return `${buildFormFieldVariableName(formName, fieldType, occurrenceIndex)}_label`
}

export function fieldTypeSupportsOptionLabelExport(fieldType) {
  return fieldType === 'select' || fieldType === 'radio'
}

/**
 * @param {unknown} value
 * @param {Array<{ value?: unknown, label?: string }>} options
 */
export function resolveOptionDisplayLabel(value, options) {
  const valueStr = value === null || value === undefined ? '' : String(value)
  if (!Array.isArray(options)) {
    return valueStr
  }

  for (const option of options) {
    if (!option || typeof option !== 'object') continue
    const optionValue = option.value === null || option.value === undefined ? '' : String(option.value)
    if (optionValue === valueStr) {
      const optionLabel = option.label ?? valueStr
      return String(optionLabel)
    }
  }

  return valueStr
}

/**
 * @param {string} formName
 * @param {Array<{ id?: string, label?: string, type?: string, options?: unknown[], defaultValue?: unknown }>} fields
 * @param {{ source?: string }} [options]
 */
export function mapFormFieldsToVariables(formName, fields, options = {}) {
  if (!Array.isArray(fields) || fields.length === 0) return []

  const resolvedFormName = formName || 'Form'
  const fieldTypeCounts = {}
  const result = []
  const sourceLabel = options.source || resolvedFormName

  for (const field of fields) {
    if (!field?.id || !field?.label) continue

    const fieldType = field.type || 'text'
    fieldTypeCounts[fieldType] = (fieldTypeCounts[fieldType] || 0) + 1
    const occurrence = fieldTypeCounts[fieldType]
    const name = buildFormFieldVariableName(resolvedFormName, fieldType, occurrence)

    result.push({
      name,
      value: field.id,
      label: String(field.label),
      type: 'form_field',
      source: sourceLabel,
      fieldType,
      options: field.options || [],
      defaultValue: field.defaultValue ?? '',
    })

    if (fieldTypeSupportsOptionLabelExport(fieldType)) {
      result.push({
        name: buildFormFieldLabelVariableName(resolvedFormName, fieldType, occurrence),
        value: field.id,
        label: `${String(field.label)} (option label)`,
        type: 'form_field',
        variant: 'option_label',
        source: sourceLabel,
        fieldType,
        options: field.options || [],
        defaultValue: field.defaultValue ?? '',
      })
    }
  }

  return result
}

function taskRequiresPostCompletionForm(taskNode) {
  const settings = taskNode?.data?.settings || {}
  if (settings.require_post_completion_form !== undefined) {
    return Boolean(settings.require_post_completion_form)
  }
  return Boolean(taskNode?.data?.requirePostCompletionForm)
}

function getConfiguredPostCompletionFormId(taskNode) {
  const settings = taskNode?.data?.settings || {}
  const fromSettings = settings.post_completion_form_id ?? taskNode?.data?.postCompletionFormId
  if (fromSettings !== null && fromSettings !== undefined && String(fromSettings).trim() !== '') {
    return String(fromSettings).trim()
  }
  return ''
}

/**
 * @param {Array<{ id: string, type?: string, data?: Record<string, unknown> }>} nodes
 * @param {Array<{ source: string, target: string }>} edges
 * @param {{ id: string, type?: string, data?: Record<string, unknown> }} taskNode
 */
export function findFormNodeForPostCompletionTask(nodes, edges, taskNode) {
  if (!nodes?.length || !edges || !taskNode?.id) return null

  const outgoingEdges = edges.filter((edge) => edge.source === taskNode.id)
  for (const edge of outgoingEdges) {
    const targetNode = nodes.find((node) => node.id === edge.target)
    if (targetNode?.type === 'formNode' && Array.isArray(targetNode.data?.fields)) {
      return targetNode
    }
  }

  const configuredFormId = getConfiguredPostCompletionFormId(taskNode)
  if (configuredFormId) {
    return nodes.find(
      (node) =>
        node.type === 'formNode' &&
        String(node.data?.form_id ?? node.data?.formId ?? '').trim() === configuredFormId
    ) ?? null
  }

  return null
}

/**
 * @param {Array<{ id: string, type?: string, data?: Record<string, unknown> }>} nodes
 * @param {Array<{ source: string, target: string }>} edges
 * @param {{ id: string, type?: string, data?: Record<string, unknown> }} taskNode
 */
export function resolvePostCompletionFormFields(nodes, edges, taskNode) {
  if (!taskRequiresPostCompletionForm(taskNode)) {
    return []
  }

  const formNode = findFormNodeForPostCompletionTask(nodes, edges, taskNode)
  if (!formNode || !Array.isArray(formNode.data?.fields)) {
    return []
  }

  const formName = formNode.data?.name || formNode.data?.label || 'Form'
  return mapFormFieldsToVariables(formName, formNode.data.fields, {
    source: `${formName} (post-completion)`,
  })
}

function deduplicateFormFieldsByName(formFields) {
  const unique = new Map()
  for (const field of formFields) {
    if (!field?.name || unique.has(field.name)) continue
    unique.set(field.name, field)
  }
  return Array.from(unique.values())
}

/**
 * @param {Array<{ id: string, type?: string, data?: Record<string, unknown> }>} nodes
 * @param {Array<{ source: string, target: string }>} edges
 * @param {string} startNodeId
 */
export function collectUpstreamFormFieldVariables(nodes, edges, startNodeId) {
  if (!nodes?.length || !edges || !startNodeId) return []

  const formFields = []
  const visitedNodes = new Set()

  const findUpstreamFormNodes = (nodeId) => {
    if (visitedNodes.has(nodeId)) return
    visitedNodes.add(nodeId)

    const node = nodes.find((n) => n.id === nodeId)
    if (!node) return

    if (node.type === 'formNode' && Array.isArray(node.data?.fields)) {
      const formName = node.data?.name || node.data?.label || 'Form'
      formFields.push(...mapFormFieldsToVariables(formName, node.data.fields))
    }

    if (node.type === 'taskNode') {
      formFields.push(...resolvePostCompletionFormFields(nodes, edges, node))
    }

    edges
      .filter((edge) => edge.target === nodeId)
      .forEach((edge) => findUpstreamFormNodes(edge.source))
  }

  findUpstreamFormNodes(startNodeId)
  return deduplicateFormFieldsByName(formFields)
}

/**
 * Returns true when any node upstream of startNodeId is a task node.
 * Used to decide whether task-completion tokens ({{completed_by_*}}) are valid,
 * since those only resolve after an upstream task has been completed.
 *
 * @param {Array<{ id: string, type?: string }>} nodes
 * @param {Array<{ source: string, target: string }>} edges
 * @param {string} startNodeId
 */
export function hasUpstreamTaskNode(nodes, edges, startNodeId) {
  if (!nodes?.length || !edges || !startNodeId) return false

  const visitedNodes = new Set()
  const stack = edges
    .filter((edge) => edge.target === startNodeId)
    .map((edge) => edge.source)

  while (stack.length > 0) {
    const nodeId = stack.pop()
    if (visitedNodes.has(nodeId)) continue
    visitedNodes.add(nodeId)

    const node = nodes.find((n) => n.id === nodeId)
    if (!node) continue

    if (node.type === 'taskNode') return true

    edges
      .filter((edge) => edge.target === nodeId)
      .forEach((edge) => stack.push(edge.source))
  }

  return false
}

/** Label for native &lt;select&gt; options (If/Else condition field picker). */
export function formatFormFieldSelectOptionLabel(field, { icon = '' } = {}) {
  const label = field.label || field.name
  const token = field.name ? `{{${field.name}}}` : ''
  const prefix = icon ? `${icon} ` : ''
  const suffix = field.variant === 'option_label' ? ' · option display text' : ''
  return token ? `${prefix}${label} · ${token}${suffix}` : `${prefix}${label}${suffix}`
}
